import React from "react";
import { PageLayout, Clause, Callout, Bullets } from "@/components/PageLayout";

/** Indonesian edition of the Privacy Policy. Kept as its own document, as legal texts usually are. */
export default function PrivacyPolicyId() {
  return (
    <PageLayout
      testId="privacy-policy-page"
      eyebrow="Legal"
      title="Kebijakan Privasi"
      updated="1 Juni 2026"
      intro="Seleksa dibangun dengan jejak data yang sengaja dibuat seminimal mungkin. Kebijakan ini menjelaskan persis apa yang kami simpan, apa yang tidak pernah kami sentuh, dan bagaimana tanggung jawab dibagi antara Seleksa dan studio foto yang bekerja dengan Anda."
    >
      <Clause n={1} title="Ringkasnya">
        <Bullets items={[
          "File asli resolusi tinggi Anda (RAW dan JPG) tidak pernah sampai ke server kami.",
          "Kami hanya menyimpan pratinjau resolusi web terkompresi yang diperlukan untuk menampilkan galeri di peramban.",
          "Penyortiran file terjadi di komputer studio sendiri, memakai skrip yang kami hasilkan untuk mereka.",
          "Studio memiliki hubungan dengan kliennya dan memiliki datanya; Seleksa hanya mengolahnya atas instruksi studio.",
          "Kami tidak pernah menjual data pribadi, dan tidak pernah memakai foto galeri untuk melatih model kecerdasan buatan.",
        ]} />
      </Clause>

      <Clause n={2} title="Arsitektur Zero-Server-Load">
        <p>
          Sebagian besar platform galeri mewajibkan studio mengunggah seluruh hasil pemotretan sebelum klien bisa
          melihatnya. Seleksa tidak bekerja seperti itu. Studio membiarkan hasil pemotretan tetap di tempatnya —
          biasanya folder Google Drive milik mereka sendiri — dan Seleksa hanya merujuknya.
        </p>
        <Callout>
          Seleksa tidak menerima, mengolah, mengubah format, mengarsipkan, atau mencadangkan file asli resolusi tinggi
          Anda. Semuanya tetap berada di penyimpanan milik studio.
        </Callout>
        <p>
          Yang dimuat peramban klien adalah gambar proksi resolusi web terkompresi, semata untuk ditampilkan. Pratinjau
          ini ada agar klien bisa melihat dan memilih foto dari ponsel atau laptop tanpa mengunduh data bergiga-giga.
          Kualitasnya tidak layak untuk dicetak atau disebarkan, dan itulah satu-satunya data gambar yang terkait
          dengan galeri Anda di sistem kami.
        </p>
        <p>
          Konsekuensinya kami rancang dengan sengaja: tidak ada arsip besar karya asli Anda di infrastruktur yang tidak
          Anda kendalikan, tidak ada masa tunggu unggahan sebelum klien mulai memilih, dan dampak yang jauh lebih kecil
          seandainya ada komponen yang bermasalah.
        </p>
      </Clause>

      <Clause n={3} title="Penyortiran file lokal — tidak ada yang dikirimkan">
        <p>
          Setelah klien selesai memilih, Seleksa membuat skrip otomatisasi kecil (file <code>.bat</code> di Windows atau
          <code>.sh</code> di macOS dan Linux) yang diunduh studio lalu dijalankan di komputernya sendiri. Skrip itu
          membaca folder lokal studio, kemudian menyalin atau memindahkan foto terpilih ke struktur folder yang rapi.
        </p>
        <Callout>
          Skrip ini berjalan sepenuhnya di perangkat studio. Skrip tidak mengunggah, mengirim, atau melaporkan isi file
          apa pun kembali ke Seleksa maupun pihak ketiga. Kami tidak pernah melihat file yang disentuhnya.
        </Callout>
        <p>
          Skrip hanya memuat nama file yang dipilih klien dan nama folder tujuan. Studio sebaiknya memeriksa skrip
          sebelum menjalankannya, dan bertanggung jawab atas eksekusinya di perangkat sendiri — lihat Syarat Layanan
          kami.
        </p>
      </Clause>

      <Clause n={4} title="Pengendali dan Pengolah Data: siapa bertanggung jawab atas apa">
        <p>
          Seleksa menjalankan layanan business-to-business-to-consumer. Studio foto berlangganan Seleksa, lalu klien
          studio itu memakai galeri yang dibuat studio. Artinya ada dua peran berbeda, dan pembedaan ini penting secara
          hukum.
        </p>
        <Bullets items={[
          "Studio adalah Pengendali Data (Data Controller). Studio menentukan klien mana yang diundang, foto mana yang ditampilkan, data pribadi apa yang dimasukkan, dan berapa lama hubungan itu berjalan. Studio bertanggung jawab memiliki dasar yang sah untuk mengolah data kliennya dan menjawab permintaan privasi kliennya.",
          "Seleksa adalah Pengolah Data (Data Processor). Kami menyimpan dan mengolah data itu semata untuk menyediakan layanan yang diminta studio. Kami bertindak atas instruksi studio dan tidak memakai data klien mereka untuk kepentingan kami sendiri.",
        ]} />
        <p>
          Jika Anda klien fotografi dan ingin data Anda dikoreksi atau dihapus, silakan hubungi studio yang mengirimkan
          tautan galeri Anda — merekalah yang mengendalikan catatan itu. Jika Anda menghubungi kami langsung, kami akan
          mengarahkan Anda ke studio terkait, karena bertindak sendiri atas data mereka akan melanggar perjanjian kami
          dengan studio.
        </p>
      </Clause>

      <Clause n={5} title="Apa yang sebenarnya kami simpan">
        <p>Untuk studio yang berlangganan (pelanggan langsung kami):</p>
        <Bullets items={[
          "Data akun: nama studio, nama pemilik dan staf, alamat email, kata sandi terenkripsi satu arah, dan pengenal akun Google bila memakai masuk lewat Google.",
          "Catatan komersial: tingkat langganan, riwayat pembayaran dan nomor referensi transaksi dari penyedia pembayaran kami, invoice, serta kode promo yang dipakai.",
          "Konfigurasi: katalog harga, ukuran cetak, template proyek, dan identitas visual studio seperti logo yang diunggah.",
        ]} />
        <p>Untuk klien fotografi yang memakai galeri (klien dari pelanggan kami):</p>
        <Bullets items={[
          "Identitas dan kontak yang dimasukkan studio: nama, alamat email, dan nomor WhatsApp.",
          "Data pilihan: foto mana yang dipilih, mana yang ditandai sebagai tambahan, ukuran cetak dan kode pigura yang diminta, serta catatan atau permintaan revisi yang dikirim.",
          "Catatan akses galeri: token berbagi privat, kode akses bila diterbitkan, dan waktu galeri dibuka serta dikirim.",
          "Pratinjau resolusi web terkompresi dari foto di galeri tersebut.",
        ]} />
        <p>
          Kami tidak pernah meminta, dan tidak berkepentingan atas, nomor kartu pembayaran klien fotografi. Uang yang
          menjadi kewajiban klien kepada studio diselesaikan langsung di antara mereka, di luar Seleksa.
        </p>
      </Clause>

      <Clause n={6} title="Pembayaran">
        <p>
          Pembayaran langganan studio diproses oleh Midtrans, penyedia gerbang pembayaran berlisensi di Indonesia,
          atau — untuk studio di luar Indonesia — oleh PayPal.
          Nomor kartu, kredensial bank, dan otentikasi e-wallet ditangani di lingkungan Midtrans atau PayPal dan tidak pernah
          dikirim ke atau disimpan oleh Seleksa. Kami hanya menyimpan nomor transaksi, jumlah, metode, dan statusnya
          untuk keperluan rekonsiliasi langganan dan penerbitan kuitansi.
        </p>
      </Clause>

      <Clause n={7} title="Email dan perpesanan">
        <p>
          Kami mengirim email transaksional — undangan galeri, ringkasan pilihan, pengingat, dan pemberitahuan
          langganan — melalui penyedia pengiriman email pihak ketiga yang bertindak sebagai sub-pengolah kami. Pesan
          WhatsApp tidak dikirim oleh server kami: Seleksa menyiapkan pesan yang sudah terisi lalu membukanya di akun
          WhatsApp studio, sehingga studio mengirimkannya dengan identitasnya sendiri.
        </p>
        <p>
          Kami tidak akan pernah mengirim email yang meminta kata sandi, kode verifikasi, atau detail pembayaran Anda.
          Pesan semacam itu bukan dari kami.
        </p>
      </Clause>

      <Clause n={8} title="Cookie dan sesi">
        <p>
          Saat Anda masuk, kami memasang satu cookie sesi yang esensial. Cookie itu ditandai <code>httpOnly</code>,
          artinya JavaScript di halaman tidak bisa membacanya — pertahanan yang kami pilih terhadap pencurian sesi
          melalui serangan cross-site scripting. Kami juga menyimpan preferensi bahasa Anda secara lokal di peramban.
        </p>
        <p>
          Kami tidak memakai cookie iklan, pelacak lintas situs, atau analitik pihak ketiga yang memprofilkan Anda di
          situs lain.
        </p>
      </Clause>

      <Clause n={9} title="Masa simpan dan penghapusan">
        <p>
          Data galeri memiliki masa simpan otomatis. Setelah sebuah proyek melewati masa itu, proses terjadwal akan
          menghapus pratinjau gambarnya dan menandai proyek sebagai kedaluwarsa, sambil menyisakan catatan administratif
          minimal — judul proyek, tanggal, dan total — agar studio tetap punya riwayat pekerjaan yang mereka tagihkan.
        </p>
        <p>
          Studio dapat menghapus proyek kapan saja dari dasbornya. Saat langganan dihentikan, data akun studio hanya
          kami simpan selama periode yang diwajibkan aturan pajak dan akuntansi Indonesia, lalu dihapus.
        </p>
      </Clause>

      <Clause n={10} title="Keamanan">
        <p>
          Akses di dalam Seleksa dibatasi per studio: setiap kueri basis data terikat pada pengenal studio yang
          meminta, sehingga satu studio tidak dapat membaca proyek, klien, atau pendapatan studio lain. Peran pengguna
          membatasi lebih jauh apa yang bisa dilakukan setiap akun, dengan angka keuangan hanya terlihat oleh pemilik
          studio. Kata sandi disimpan sebagai hash satu arah bergaram dan tidak dapat dipulihkan oleh kami maupun siapa
          pun.
        </p>
        <p>
          Tautan galeri memuat token panjang yang tidak bisa diterka, dan studio dapat menambahkan kode akses per
          klien. Siapa pun yang memegang tautan galeri dapat membukanya, jadi studio sebaiknya membagikan tautan hanya
          kepada klien yang dituju dan mengganti kode akses bila tautan tersebar.
        </p>
        <p>
          Tidak ada sistem yang sepenuhnya aman. Bila kami menemukan pelanggaran data pribadi, kami akan memberi tahu
          studio yang terdampak tanpa penundaan yang tidak wajar, beserta detail yang mereka butuhkan untuk memenuhi
          kewajibannya sebagai Pengendali Data.
        </p>
      </Clause>

      <Clause n={11} title="Anak-anak">
        <p>
          Seleksa adalah alat bisnis dan tidak ditujukan untuk anak-anak. Sesi wisuda, keluarga, dan foto sekolah sering
          menampilkan anak di bawah umur sebagai subjek foto; dalam hal itu, studio bertanggung jawab memperoleh
          persetujuan dari orang tua atau wali sebelum memasukkan data mereka atau membagikan galeri.
        </p>
      </Clause>

      <Clause n={12} title="Perubahan dan kontak">
        <p>
          Kami akan memperbarui kebijakan ini seiring layanan berkembang, dan mengubah tanggal di bagian atas ketika itu
          terjadi. Perubahan penting yang memengaruhi studio pelanggan akan kami sampaikan lewat email.
        </p>
        <p>
          Pertanyaan tentang kebijakan ini, atau tentang permintaan data tertentu, dapat dikirim ke{" "}
          <a href="mailto:seleksa.com@gmail.com" className="text-[#D4AF37] hover:underline">seleksa.com@gmail.com</a>,
          atau ke Jl. Pasar 1, Komp SBE (Setia Budi Estate), Kota Medan, Sumatera Utara, Indonesia.
        </p>
      </Clause>
    </PageLayout>
  );
}
